<template>
  <div>
    <div class="steps_body">
      <p class="t-h--1">{{ $t("Resultados") }}</p>

      <template v-if="calculationSolutions.length">
        <div class="u-df u-jcsb u-aifs">
          <p v-if="calculationSolutions.length" class="lead">
            {{
              $t(
                "Encontrámos {totalResults} resultado(s) que correspondem aos seus critérios de simulação. Seleccione o produto que melhor se adequar ao seu projecto.",
                null,
                { totalResults: calculationSolutions.length }
              )
            }}
          </p>

          <app-dropdown :toggle-text="$t('Filtrar')">
            <div class="filters__block">
              <div class="filters__row">
                <p
                  class="
                    filters__row_title
                    t-label t-label--small t-label--white
                  "
                >
                  {{ $t("Secção") }}
                </p>

                <b-field>
                  <b-slider
                    class="filters__slider"
                    v-model="sectionFilter"
                    :min="sectionFilterLimits[0]"
                    :max="sectionFilterLimits[1]"
                    :step="50"
                  >
                    <b-slider-tick :value="sectionFilterLimits[0]">{{
                      sectionFilterLimits[0]
                    }}</b-slider-tick>
                    <b-slider-tick :value="sectionFilterLimits[1]">{{
                      sectionFilterLimits[1]
                    }}</b-slider-tick>
                  </b-slider>
                </b-field>
              </div>

              <div class="filters__row">
                <p
                  class="
                    filters__row_title
                    t-label t-label--small t-label--white
                  "
                >
                  {{ $t("Espessura (mm)") }}
                </p>

                <b-field>
                  <b-slider
                    class="filters__slider"
                    v-model="thicknessFilter"
                    :min="thicknessFilterLimits[0]"
                    :max="thicknessFilterLimits[1]"
                    :step="0.5"
                  >
                    <b-slider-tick :value="thicknessFilterLimits[0]">{{
                      thicknessFilterLimits[0]
                    }}</b-slider-tick>
                    <b-slider-tick :value="thicknessFilterLimits[1]">{{
                      thicknessFilterLimits[1]
                    }}</b-slider-tick>
                  </b-slider>
                </b-field>
              </div>

              <div class="filters__row">
                <p
                  class="
                    filters__row_title
                    t-label t-label--small t-label--white
                  "
                >
                  {{ $t("Aço") }}
                </p>

                <b-checkbox-group>
                  <b-checkbox
                    v-for="steel in solutionsSteels"
                    v-model="steelFilter"
                    :native-value="steel"
                    :key="steel"
                  >
                    {{ steel }}
                  </b-checkbox>
                </b-checkbox-group>
              </div>
            </div>
          </app-dropdown>
        </div>

        <calc-solutions-table
          v-model="selectedSolution"
          :calculation="rawCalculation"
          :solutions="filteredCalculationSolutions"
        />
      </template>

      <template v-else>
        <p class="lead">
          {{
            $t(
              "Não encontrámos nenhum resultado que correspondesse aos seus critérios de simulação. Por favor contacte-nos para que o possamos ajudar."
            )
          }}
        </p>
      </template>
    </div>

    <div class="steps_nav">
      <b-button
        class="steps_nav__btn"
        @click="$emit('prev')"
        icon-left="arrow-left"
        size="is-medium"
        type="is-primary"
        inverted
        >{{ $t("Voltar") }}</b-button
      >

      <div>
        <b-button
          class="steps_nav__btn"
          @click="handleGetCalculationNoteClick"
          :loading="fetchingUsersCalculationNote"
          size="is-medium"
          type="is-primary"
          inverted
          :disabled="!this.selectedSolution"
          >{{ $t("Nota de cálculo") }}</b-button
        >
        <b-button
          class="steps_nav__btn"
          @click="handleSaveBtnClick"
          size="is-medium"
          type="is-primary"
          :disabled="
            !this.selectedSolution ||
            this.selectedSolution === this.savedSolution
          "
          >{{ $t("Guardar resultado") }}</b-button
        >
      </div>
    </div>

    <b-modal :active.sync="isModalNameCalcOpen">
      <modal-name-calc @save="handleModalNameCalcSave" />
    </b-modal>

    <b-modal :active.sync="isModalAuthOpen">
      <modal-auth @login="handleModalAuthLogin" />
    </b-modal>
  </div>
</template>

<script>
import CalcSolutionsTable from "@/components/CalcSolutionsTable";
import { filterCalculationSolutions } from "@/helpers";
import { mapActions, mapState } from "vuex";
import { withoutDuplicates } from "@/utils";
import { ModalNameCalc, ModalAuth } from "@/components";

export default {
  name: "CalcStepResults",

  components: {
    CalcSolutionsTable,
    ModalAuth,
    ModalNameCalc,
  },

  data() {
    const sectionFilterLimits = [50, 300];
    const thicknessFilterLimits = [1.5, 3];

    return {
      isModalNameCalcOpen: false,
      isModalAuthOpen: false,

      thicknessFilterLimits,
      sectionFilterLimits,

      thicknessFilter: [...thicknessFilterLimits],
      sectionFilter: [...sectionFilterLimits],
      steelFilter: [],

      savedSolution: null,
      selectedSolution: null,
      calculationDescription: "",

      fetchingUsersCalculationNote: false,
      downloadPdfLink: undefined,
    };
  },

  computed: {
    ...mapState([
      "user",
      "usersCalculations",
      "usersCalculationsNotes",
      "selectedLang",
    ]),
    ...mapState("calculator", ["rawCalculationId"]),

    rawCalculation() {
      return this.usersCalculations.find(
        ({ id }) => id === this.rawCalculationId
      );
    },

    calculationSolutions() {
      return this.rawCalculation.output.solutions;
    },

    calculationNote() {
      return this.usersCalculationsNotes.find(
        ({ id }) => id === this.rawCalculationId
      );
    },

    filteredCalculationSolutions() {
      return filterCalculationSolutions(this.calculationSolutions, {
        minSection: this.sectionFilter[0],
        maxSection: this.sectionFilter[1],
        minThickness: this.thicknessFilter[0],
        maxThickness: this.thicknessFilter[1],
        steels: this.steelFilter,
      });
    },

    solutionsSteels() {
      return withoutDuplicates(
        this.calculationSolutions.map(({ steel }) => steel)
      );
    },

    isSolutionSaved() {
      return (solution) => {
        return this.savedSolution === solution;
      };
    },
  },

  watch: {
    filteredCalculationSolutions(val) {
      if (!val.includes(this.selectedSolution)) {
        this.selectedSolution = null;
      }
    },
  },

  methods: {
    ...mapActions(["fetchUsersCalculationNote", "updateUsersCalculation"]),

    handleGetCalculationNoteClick() {
      if (!this.isSolutionSaved(this.selectedSolution)) {
        this.$buefy.notification.open({
          queue: false,
          message: this.$t(
            "Grave primeiro uma simulação para poder imprimir a sua nota de cálculo."
          ),
          duration: 4000,
          position: "is-bottom-right",
          type: "is-warning",
          hasIcon: true,
        });

        return;
      }

      if (this.calculationNote) {
        //download calc
        this.triggerPdfDownload();
      } else {
        this.fetchingUsersCalculationNote = true;

        this.fetchUsersCalculationNote({
          id: this.rawCalculation.id,
          type: this.rawCalculation.input.type,
          lang: this.selectedLang,
        })
          .then(() => {
            this.downloadPdfLink = document.createElement("a");
            this.downloadPdfLink.href = `data:application/octet-stream;base64,${this.calculationNote.pdfFile}`;
            this.downloadPdfLink.download = `calculo-${this.rawCalculation.Description}.pdf`;
            this.triggerPdfDownload();
          })
          .catch(() => {
            this.$buefy.notification.open({
              queue: false,
              message: this.$t(
                "Ocorreu um erro a tentar buscar a sua nota de cálculo. Por favor tente novamente. Se o erro persistir contacte-nos através de comercial@constalica.com"
              ),
              duration: 4000,
              position: "is-bottom-right",
              type: "is-danger",
              hasIcon: true,
            });
          })
          .finally(() => {
            this.fetchingUsersCalculationNote = false;
          });
      }
    },

    triggerPdfDownload() {
      this.downloadPdfLink.click();
    },

    handleSaveBtnClick() {
      if (this.isSolutionSaved(this.selectedSolution)) {
        this.$buefy.notification.open({
          queue: false,
          message: this.$t(
            "Esta solução já se encontra gravada. Aceda-a em 'A Minha Conta' ou clique em 'Nota de Cálculo' para fazer o seu download em formato PDF."
          ),
          duration: 4000,
          position: "is-bottom-right",
          type: "is-warning",
          hasIcon: true,
        });

        return;
      }

      this.isModalNameCalcOpen = true;
    },

    handleModalNameCalcSave({ name }) {
      this.isModalNameCalcOpen = false;
      this.calculationDescription = name;

      if (this.user) {
        this.saveSolution({
          solution: this.selectedSolution,
          Description: this.calculationDescription,
        });
      } else {
        this.isModalAuthOpen = true;
      }
    },

    handleModalAuthLogin() {
      this.isModalAuthOpen = false;

      this.saveSolution({
        solution: this.selectedSolution,
        Description: this.calculationDescription,
      });
    },

    saveSolution({ solution, Description }) {
      this.updateUsersCalculation({
        id: this.rawCalculation.id,
        updates: {
          Description,
          chosenSolution: solution.id,
        },
      })
        .then(() => {
          this.savedSolution = solution;

          this.$buefy.notification.open({
            queue: false,
            message: this.$t(
              "A solução foi gravada com sucesso. Clique em 'Nota de Cálculo' para fazer o seu download em formato PDF. Aceda-a a qualquer altura em 'A Minha Conta'"
            ),
            duration: 4000,
            position: "is-bottom-right",
            type: "is-success",
            hasIcon: true,
          });
        })
        .catch(() => {
          this.$buefy.notification.open({
            queue: false,
            message: this.$t(
              "Ocorreu um erro a tentar gravar o seu cálculo. Por favor tente novamente. Se o erro persistir contacte-nos através de comercial@constalica.com"
            ),
            duration: 4000,
            position: "is-bottom-right",
            type: "is-error",
            hasIcon: true,
          });
        });
    },
  },
  created() {
    this.steelFilter = [...this.solutionsSteels];
  },
};
</script>

<style lang="scss" scoped>
.lead {
  max-width: 467px;
  margin-bottom: 50px;
}

.filters__block {
  width: 222px;
}

.filters__row:not(:last-child) {
  margin-bottom: 3em;
}

.filters__row_title {
  margin-bottom: 8px;
}

.filters__slider {
  margin-right: 5px;
  margin-left: 5px;
  width: auto;
}

//first
.filters__slider .b-slider-tick .b-slider-tick-label {
  left: -3px;
  transform: none;
}

//second
.filters__slider .b-slider-tick + .b-slider-tick .b-slider-tick-label {
  left: auto;
  right: -3px;
}
</style>
